import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ImageLabelService from '../../services/ImageLabelService';

export const fetchImageLabels = createAsyncThunk(
    'imagelabels/fetch',
    async ({ modelId, status, classLabel, limit = 50, skip = 0 }) => {
        return ImageLabelService.list({ modelId, status, classLabel, limit, skip });
    },
);

export const updateImageLabel = createAsyncThunk(
    'imagelabels/update',
    async ({ id, action, classLabel, bbox }) => {
        return ImageLabelService.update(id, { action, classLabel, bbox });
    },
);

export const batchImageLabels = createAsyncThunk(
    'imagelabels/batch',
    async ({ ids, action }) => {
        const result = await ImageLabelService.batch({ ids, action });
        return { ids, action, ...result };
    },
);

export const initialState = {
    items: [],
    total: 0,
    counts: { pending: 0, accepted: 0, rejected: 0, total: 0 },
    filter: { status: 'pending', classLabel: '' },
    paging: { skip: 0, limit: 50 },
    loading: false,
    error: null,
    selectedIds: [],
};

export const ImageLabelSlice = createSlice({
    name: 'imagelabel',
    initialState,
    reducers: {
        setStatusFilter: (state, action) => {
            state.filter.status = action.payload;
            state.paging.skip = 0;
            state.selectedIds = [];
        },
        setClassLabelFilter: (state, action) => {
            state.filter.classLabel = action.payload;
            state.paging.skip = 0;
        },
        setPageSkip: (state, action) => {
            state.paging.skip = Math.max(0, action.payload);
        },
        toggleSelection: (state, action) => {
            const id = action.payload;
            const idx = state.selectedIds.indexOf(id);
            if (idx >= 0) {
                state.selectedIds.splice(idx, 1);
            } else {
                state.selectedIds.push(id);
            }
        },
        clearSelection: (state) => {
            state.selectedIds = [];
        },
        selectAllVisible: (state) => {
            state.selectedIds = state.items.map((i) => i._id);
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchImageLabels.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchImageLabels.fulfilled, (state, action) => {
                state.loading = false;
                state.items = action.payload.items || [];
                state.total = action.payload.total || 0;
                state.counts = action.payload.counts || initialState.counts;
            })
            .addCase(fetchImageLabels.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(updateImageLabel.fulfilled, (state, action) => {
                const updated = action.payload;
                if (!updated || !updated._id) return;
                const idx = state.items.findIndex((i) => i._id === updated._id);
                if (idx >= 0) state.items[idx] = updated;
            })
            .addCase(batchImageLabels.fulfilled, (state, action) => {
                const { ids, action: act } = action.payload;
                const set = new Set(ids);
                for (const item of state.items) {
                    if (!set.has(item._id)) continue;
                    if (act === 'accept') {
                        item.rejected = false;
                        item.acceptedAt = Date.now();
                    } else if (act === 'reject') {
                        item.rejected = true;
                        delete item.acceptedAt;
                    }
                }
                state.selectedIds = [];
            });
    },
});

export const {
    setStatusFilter,
    setClassLabelFilter,
    setPageSkip,
    toggleSelection,
    clearSelection,
    selectAllVisible,
} = ImageLabelSlice.actions;

export const selectImageLabelState = (state) => state.imagelabel;
export const selectImageLabels = (state) => state.imagelabel.items;
export const selectImageLabelCounts = (state) => state.imagelabel.counts;

export default ImageLabelSlice.reducer;
